import { useState } from "react";
import { Link } from "react-router-dom";
import { Plus, FileSpreadsheet, FileText, Receipt } from "lucide-react";
import toast from "react-hot-toast";
import {
  useInvoices,
  useIssueInvoice,
  useMarkInvoicePaid,
  useVoidInvoice,
  useDeleteInvoice,
  INVOICE_STATUS_LABEL,
  InvoiceStatus,
  invoiceClientName,
  invoiceBalance,
} from "@/api/invoices";
import { useGenerateDocument } from "@/api/documentGenerator";
import { downloadDocumentBytesGlobal } from "@/api/documents";
import { useAuthStore } from "@/stores/authStore";
import { getApiErrorMessage } from "@/api/client";
import { downloadFile } from "@/utils/downloadFile";
import CreateInvoiceModal from "./CreateInvoiceModal";

const STATUS_STYLE: Record<InvoiceStatus, string> = {
  BORRADOR: "bg-gray-100 text-gray-600",
  EMITIDA: "bg-blue-100 text-blue-700",
  PARCIAL: "bg-amber-100 text-amber-700",
  PAGADA: "bg-green-100 text-green-700",
  VENCIDA: "bg-red-100 text-red-700",
  ANULADA: "bg-gray-200 text-gray-500",
};

const STATUS_OPTIONS = Object.keys(INVOICE_STATUS_LABEL) as InvoiceStatus[];

const currency = new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP", maximumFractionDigits: 0 });

export default function InvoicesPage() {
  const [status, setStatus] = useState<InvoiceStatus | "">("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [showCreate, setShowCreate] = useState(false);
  const hasPermission = useAuthStore((s) => s.hasPermission);

  const { data, isLoading } = useInvoices({ status: status || undefined, search: search || undefined, page, pageSize: 15 });
  const issueInvoice = useIssueInvoice();
  const markPaid = useMarkInvoicePaid();
  const voidInvoice = useVoidInvoice();
  const deleteInvoice = useDeleteInvoice();

  const invoices = data?.data ?? [];
  const meta = data?.meta;

  const handleIssue = async (id: string) => {
    try {
      await issueInvoice.mutateAsync(id);
      toast.success("Factura emitida.");
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  const handleMarkPaid = async (id: string) => {
    if (!window.confirm("¿Marcar esta factura como pagada en su totalidad?")) return;
    try {
      await markPaid.mutateAsync(id);
      toast.success("Factura marcada como pagada.");
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  const handleVoid = async (id: string) => {
    if (!window.confirm("¿Anular esta factura?")) return;
    try {
      await voidInvoice.mutateAsync(id);
      toast.success("Factura anulada.");
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("¿Eliminar esta factura en borrador?")) return;
    try {
      await deleteInvoice.mutateAsync(id);
      toast.success("Factura eliminada.");
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  const handleExportExcel = async () => {
    try {
      await downloadFile("/invoices/export.xlsx", "facturas.xlsx", { status: status || undefined, search: search || undefined });
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  const generateDocument = useGenerateDocument();

  // Cuenta de cobro / Factura membreteadas (rediseño navy/dorado) —
  // reemplazan el .docx plano anterior: se generan como PDF real con el
  // Generador de Documentos y quedan guardadas en el expediente del cliente.
  const handleCollectionLetter = async (invoiceId: string) => {
    try {
      const result = await generateDocument.mutateAsync({ docType: "CUENTA_COBRO", invoiceId });
      await downloadDocumentBytesGlobal(result.document.id);
      toast.success(`Cuenta de cobro ${result.numeroLabel} generada y guardada en el expediente del cliente.`);
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  const handleInvoicePdf = async (invoiceId: string) => {
    try {
      const result = await generateDocument.mutateAsync({ docType: "FACTURA", invoiceId });
      await downloadDocumentBytesGlobal(result.document.id);
      toast.success(`Factura ${result.numeroLabel} generada y guardada en el expediente del cliente.`);
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Facturación</h1>
          <p className="text-sm text-gray-500">Borrador → Emitida → Pagada, con vencimiento automático y anulación.</p>
        </div>
        <div className="flex gap-2">
          {hasPermission("invoices.export") && (
            <button type="button" className="btn-secondary" onClick={handleExportExcel}>
              <FileSpreadsheet size={16} className="mr-1.5" /> Exportar Excel
            </button>
          )}
          {hasPermission("invoices.create") && (
            <button type="button" className="btn-primary" onClick={() => setShowCreate(true)}>
              <Plus size={16} className="mr-1.5" /> Nueva factura
            </button>
          )}
        </div>
      </div>

      <div className="card flex flex-wrap items-center gap-3 p-4">
        <input
          type="search"
          placeholder="Buscar por notas..."
          className="input-field min-w-[220px] flex-1"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
        />
        <select
          className="input-field w-auto"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value as InvoiceStatus | "");
            setPage(1);
          }}
        >
          <option value="">Todos los estados</option>
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {INVOICE_STATUS_LABEL[s]}
            </option>
          ))}
        </select>
      </div>

      <div className="card overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-gray-500">N.°</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Cliente</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Expedición</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Vencimiento</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Total</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Estado</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading && (
              <tr>
                <td colSpan={7} className="px-4 py-6 text-center text-gray-400">
                  Cargando...
                </td>
              </tr>
            )}
            {!isLoading && invoices.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-10 text-center text-gray-400">
                  No hay facturas que coincidan con los filtros.
                </td>
              </tr>
            )}
            {invoices.map((invoice) => (
              <tr key={invoice.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 font-mono text-gray-500">{invoice.invoiceNumber}</td>
                <td className="px-4 py-3 text-gray-600">
                  {invoice.client ? (
                    <Link to={`/clients/${invoice.client.id}`} className="hover:text-brand-600">
                      {invoiceClientName(invoice)}
                    </Link>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </td>
                <td className="px-4 py-3 text-gray-600">{invoice.issueDate}</td>
                <td className="px-4 py-3 text-gray-600">{invoice.dueDate}</td>
                <td className="px-4 py-3 font-medium text-gray-900">{currency.format(Number(invoice.total))}</td>
                <td className="px-4 py-3">
                  <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLE[invoice.status]}`}>
                    {INVOICE_STATUS_LABEL[invoice.status]}
                  </span>
                </td>
                <td className="px-4 py-3 text-right space-x-2 whitespace-nowrap">
                  {invoice.status === "BORRADOR" && hasPermission("invoices.approve") && (
                    <button type="button" className="text-xs font-medium text-brand-600 hover:text-brand-800" onClick={() => handleIssue(invoice.id)}>
                      Emitir
                    </button>
                  )}
                  {["EMITIDA", "PARCIAL", "VENCIDA"].includes(invoice.status) && hasPermission("invoices.update") && (
                    <button type="button" className="text-xs font-medium text-green-600 hover:text-green-800" onClick={() => handleMarkPaid(invoice.id)}>
                      Marcar pagada
                    </button>
                  )}
                  {!["PAGADA", "ANULADA"].includes(invoice.status) && hasPermission("invoices.update") && (
                    <button type="button" className="text-xs font-medium text-red-500 hover:text-red-700" onClick={() => handleVoid(invoice.id)}>
                      Anular
                    </button>
                  )}
                  {invoice.status !== "BORRADOR" && hasPermission("documents.create") && (
                    <button
                      type="button"
                      className="inline-flex items-center text-xs font-medium text-gray-500 hover:text-gray-700 disabled:opacity-50"
                      onClick={() => handleInvoicePdf(invoice.id)}
                      disabled={generateDocument.isPending}
                      title="Generar factura membreteada (PDF)"
                    >
                      <Receipt size={13} className="mr-1" /> Factura
                    </button>
                  )}
                  {!["BORRADOR", "ANULADA"].includes(invoice.status) && invoiceBalance(invoice) > 0 && hasPermission("documents.create") && (
                    <button
                      type="button"
                      className="inline-flex items-center text-xs font-medium text-gray-500 hover:text-gray-700 disabled:opacity-50"
                      onClick={() => handleCollectionLetter(invoice.id)}
                      disabled={generateDocument.isPending}
                      title="Generar cuenta de cobro membreteada (PDF)"
                    >
                      <FileText size={13} className="mr-1" /> Cuenta de cobro
                    </button>
                  )}
                  {invoice.status === "BORRADOR" && hasPermission("invoices.delete") && (
                    <button type="button" className="text-xs font-medium text-red-500 hover:text-red-700" onClick={() => handleDelete(invoice.id)}>
                      Eliminar
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {meta && meta.totalPages && meta.totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-gray-100 px-4 py-3 text-sm">
            <span className="text-gray-500">
              Página {meta.page} de {meta.totalPages} ({meta.total} facturas)
            </span>
            <div className="space-x-2">
              <button className="btn-secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                Anterior
              </button>
              <button className="btn-secondary" disabled={!!meta.totalPages && page >= meta.totalPages} onClick={() => setPage((p) => p + 1)}>
                Siguiente
              </button>
            </div>
          </div>
        )}
      </div>

      {showCreate && <CreateInvoiceModal onClose={() => setShowCreate(false)} />}
    </div>
  );
}
